import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SECTIONS } from "@/lib/departments";
import { ZigzagDivider, ZigzagDividerInverse, GeoAccents } from "@/components/GeoAccents";

const NEON = "#5BC2DE";

function SectionCard({ section, hovered, setHovered }) {
  const isHovered = hovered === section.slug;
  const isOther = hovered && hovered !== section.slug;
  return (
    <Link
      to={section.href}
      data-testid={`sport-${section.slug}-link`}
      onMouseEnter={() => setHovered(section.slug)}
      onMouseLeave={() => setHovered(null)}
      className="relative overflow-hidden flex-1 group cursor-pointer transition-[flex] duration-700 ease-out"
      style={{ flex: isHovered ? 1.35 : isOther ? 0.75 : 1 }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 ease-out group-hover:scale-110"
        style={{ backgroundImage: `url(${section.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/30" />
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-700" />

      <GeoAccents
        className="inset-0 w-full h-full opacity-0 group-hover:opacity-90 transition-opacity duration-500"
        colors={[section.colors[0], section.colors[1], section.colors[0]]}
      />

      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-12">
        <h2
          className="font-display text-[10vw] md:text-[4.5vw] leading-[0.85] uppercase text-white"
          data-testid={`sport-${section.slug}-title`}
        >
          {section.name}
        </h2>
        <p className="font-body text-white/70 max-w-md mt-4 text-sm md:text-base">
          {section.description}
        </p>
        <div
          className={`mt-6 inline-flex items-center gap-3 transition-all duration-500 ${
            isHovered ? "opacity-100 translate-x-0" : "opacity-60 -translate-x-2"
          }`}
        >
          <span
            className="csg-btn"
            style={{
              background: `linear-gradient(135deg, ${section.colors[0]} 0%, ${section.colors[1]} 100%)`,
            }}
          >
            Enter {section.name} <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function LandingSport() {
  const [hovered, setHovered] = useState(null);
  return (
    <div className="relative h-screen bg-black flex flex-col overflow-hidden">
      <header className="px-6 md:px-14 py-4 md:py-5 flex items-center justify-end shrink-0">
        <Link
          to="/admin/login"
          data-testid="admin-login-link"
          className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-white/60 hover:text-white transition-colors"
        >
          Admin
        </Link>
      </header>

      <section className="px-6 pb-5 md:pb-7 text-center shrink-0">
        <h1
          className="font-display uppercase leading-[0.9] text-[9vw] md:text-[4.2vw] whitespace-nowrap"
          data-testid="landing-title"
        >
          <span className="text-white">COMPLETE</span>{" "}
          <span style={{ color: NEON }}>SPORTS</span>{" "}
          <span className="text-white">GROUP</span>
        </h1>
        <img
          src="/assets/logos/csg-white.png"
          alt="CSG logo"
          data-testid="landing-logo"
          className="h-12 md:h-16 mx-auto mt-4"
        />
        <p className="font-display uppercase tracking-wide mt-4 text-base md:text-xl leading-none">
          <span className="text-white/85">Where The Game </span>
          <span style={{ color: NEON }}>Becomes Career.</span>
        </p>
        <p className="font-body text-white/60 max-w-2xl mx-auto mt-2 text-xs md:text-sm">
          A 360° sports agency delivering high level representation
        </p>
      </section>

      <ZigzagDivider color={NEON} height={18} />

      {/* Section selector — 3 cards */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0" data-testid="sport-split">
        {SECTIONS.map((s, i) => (
          <React.Fragment key={s.slug}>
            <SectionCard section={s} hovered={hovered} setHovered={setHovered} />
            {i < SECTIONS.length - 1 && (
              <div className="hidden md:block w-px bg-white/10" />
            )}
          </React.Fragment>
        ))}
      </div>

      <ZigzagDividerInverse color={NEON} height={18} />

      <footer className="px-6 md:px-14 py-3 flex items-center justify-between font-body text-xs tracking-[0.3em] uppercase text-white/40 shrink-0">
        <span>© {new Date().getFullYear()} CSG</span>
      </footer>
    </div>
  );
}
